import React from 'react';
import { SettingsLayoutHeader } from 'components/atoms/settingsLayoutHeader';
import { Box, Flex } from 'rebass/styled-components';

import { trackEvent } from 'helpers/analytics';
import { GoogleOAuthProvider } from '@react-oauth/google';
import usePageHook from '../../../hooks/usePageHook';
import { useIntegrationsPage } from './hooks/useIntegrationsPage';

import { LinkedinIntegrationCard } from './LinkedinIntegrationCard';
import { GoogleIntegrationCard } from './GoogleIntegrationCard';
import { PrivacyNote } from './PrivacyNote';
import { ShareContactsWarning } from './ShareContactsWarning';
import { SurveyCallout } from './SurveyCallout';

const IntegrationsContent = () => {
  usePageHook('Personal integrations');

  const {
    user,
    activeNetwork,
    googleSync,
    googleLogin,
    handleConfirmRemoveGoogle,
    handleConfirmRemoveLinkedin,
    hasGoogleSyncFeature,
    isDisconnectingLinkedin,
    isDeletingGoogleAccount,
    isUpdatingLinkedinSync,
    linkedinSync,
    getLinkedinSyncLink,
    updateLinkedinSyncStatus,
    myNetworksLink,
    shouldShowShareContactsWarning,
  } = useIntegrationsPage();

  const description = hasGoogleSyncFeature
    ? "Sync your LinkedIn and Google data with Getro. Choose which networks to share your contacts with from the 'My networks' screen."
    : "Sync your LinkedIn data with Getro. Choose which networks to share your contacts with from the 'My networks' screen.";

  return (
    <>
      <Box maxWidth={['100%', '100%', '1200px']} width="100%" pr={['0px', '0px', '80px']}>
        <SettingsLayoutHeader title="Personal integrations" description={description} />
        <Flex sx={{ flexDirection: 'column', gap: '40px', pt: '24px', width: '100%' }}>
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            {shouldShowShareContactsWarning && <ShareContactsWarning myNetworksLink={myNetworksLink} />}
            <LinkedinIntegrationCard
              linkedinSync={linkedinSync}
              getLinkedinSyncLink={getLinkedinSyncLink}
              onUpdateLinkedinSync={updateLinkedinSyncStatus}
              onRemoveConfirm={handleConfirmRemoveLinkedin}
              isRemoving={isDisconnectingLinkedin}
              isPausing={isUpdatingLinkedinSync}
            />
            {hasGoogleSyncFeature && (
              <GoogleIntegrationCard
                googleSync={googleSync}
                onConnect={() => googleLogin()}
                onReconnect={() => googleLogin()}
                onRemoveConfirm={handleConfirmRemoveGoogle}
                isRemoving={isDeletingGoogleAccount}
              />
            )}
          </Flex>

          <PrivacyNote />
        </Flex>

        <SurveyCallout
          user={user}
          activeNetwork={activeNetwork}
          onTrack={() => trackEvent('user_account:more_integrations_feedback')}
        />
      </Box>
    </>
  );
};

export const Integrations = () => (
  <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
    <IntegrationsContent />
  </GoogleOAuthProvider>
);
